import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Key, Building2, CheckCircle2, ArrowRight, Shield, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function Licensing() {
  const licensingOptions = [
    {
      title: "Individual Practitioner License",
      description: "For consultants and professionals implementing Zodiac methodologies independently.",
      features: [
        "Diagnostic instrument access",
        "Methodology documentation",
        "Practitioner toolkit",
        "Annual certification renewal"
      ],
      type: "Individual"
    },
    {
      title: "Organizational License",
      description: "For organizations implementing workforce intelligence programs internally.",
      features: [
        "Enterprise platform access",
        "Training materials for internal teams",
        "Implementation support",
        "Benchmark data access"
      ],
      type: "Enterprise"
    },
    {
      title: "Training Partner License",
      description: "For educational institutions and training providers delivering Zodiac curriculum.",
      features: [
        "Curriculum licensing rights",
        "Instructor certification",
        "Co-branding opportunities",
        "Revenue sharing model"
      ],
      type: "Partner"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="licensing-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/programs" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#991B1B] mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Programs
            </Link>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <Key className="w-4 h-4" />
                Institutional Partnership
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Licensing
              </h1>
              <p className="text-lg text-[#1E3A8A] leading-relaxed">
                License Zodiac workforce intelligence methodologies for your organization, 
                practice, or educational institution.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Licensing Options */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {licensingOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                        {option.type === 'Individual' && <Users className="w-6 h-6 text-[#1E3A8A]" />}
                        {option.type === 'Enterprise' && <Building2 className="w-6 h-6 text-[#1E3A8A]" />}
                        {option.type === 'Partner' && <Shield className="w-6 h-6 text-[#1E3A8A]" />}
                      </div>
                      <span className="px-3 py-1 rounded-full bg-[#991B1B]/10 text-[#991B1B] text-xs font-medium">
                        {option.type}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-3" style={{ color: '#991B1B' }}>
                      {option.title}
                    </h3>
                    <p className="text-[#1E3A8A] text-sm mb-6 leading-relaxed">
                      {option.description}
                    </p>
                    <div className="space-y-2 mb-6">
                      {option.features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                          <span className="text-[#1E3A8A]">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/contact">
                      <Button className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                        Inquire
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
            Partner With Us
          </h2>
          <p className="text-lg text-[#1E3A8A] mb-8">
            Contact us to discuss licensing options tailored to your needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
